import React from 'react'

interface ServiceCardProps {
  icon: React.ReactNode
  title: string
  description: string
}

const ServiceCard: React.FC<ServiceCardProps> = ({ icon, title, description }) => (
  <div className='bg-surface border border-black/[0.06] rounded-[14px] p-5 md:p-8 hover:border-[rgba(37,83,232,0.12)] hover:-translate-y-[3px] transition-all duration-300'>
    <div className='w-12 h-12 rounded-[12px] bg-[rgba(37,83,232,0.06)] flex items-center justify-center mb-6 text-pop'>
      {icon}
    </div>
    <h3 className='font-hd text-[18px] font-bold tracking-[-0.3px] mb-2'>{title}</h3>
    <p className='text-[14px] text-ink2 leading-[1.65]'>{description}</p>
  </div>
)

const Services: React.FC = () => {
  return (
    <section className='py-[5.5rem] scroll-mt-20 px-4 md:px-10 max-w-[1060px] mx-auto' id='services'>
      <div className='text-[12px] font-bold tracking-[2px] uppercase text-pop mb-3'>Services</div>
      <h2 className='font-hd text-[28px] md:text-[42px] font-extrabold tracking-[-1.5px] md:tracking-[-2px] leading-[1.08] mb-3'>
        Everything between
        <br />
        &ldquo;I have an idea&rdquo; and
        <br />
        &ldquo;it&rsquo;s live in production.&rdquo;
      </h2>
      <p className='text-[16px] text-ink2 max-w-[460px] leading-[1.7] mb-12'>
        One team. No handoffs. No finger-pointing. We own the outcome from day one.
      </p>

      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5'>
        <ServiceCard
          title='Mobile development'
          description='Native iOS, Android, and cross-platform apps built for real users — not pitch decks. Design, development, and App Store submission.'
          icon={
            <svg className='w-6 h-6' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
              <rect x='5' y='2' width='14' height='20' rx='2' />
              <line x1='12' y1='18' x2='12' y2='18.01' />
            </svg>
          }
        />
        <ServiceCard
          title='Web applications'
          description='Responsive web apps, admin dashboards, customer portals, and marketing sites. Built fast. Built right. Built to convert.'
          icon={
            <svg className='w-6 h-6' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
              <rect x='2' y='3' width='20' height='14' rx='2' />
              <line x1='8' y1='21' x2='16' y2='21' />
              <line x1='12' y1='17' x2='12' y2='21' />
            </svg>
          }
        />
        <ServiceCard
          title='Cloud & backend'
          description="APIs, databases, authentication, payments, and infrastructure. The stuff your users never see but your business can't run without."
          icon={
            <svg className='w-6 h-6' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
              <path d='M18 10h-4V4a2 2 0 0 0-4 0v6H6l6 8 6-8z' />
              <path d='M4 20h16' />
            </svg>
          }
        />
        <ServiceCard
          title='SaaS platforms'
          description='Multi-tenant architecture, subscription billing, role-based access — platforms designed to scale and generate recurring revenue.'
          icon={
            <svg className='w-6 h-6' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
              <path d='M12 2L2 7l10 5 10-5-10-5z' />
              <path d='M2 17l10 5 10-5' />
              <path d='M2 12l10 5 10-5' />
            </svg>
          }
        />
        <ServiceCard
          title='Product & UX design'
          description='User research, wireframes, prototypes, and visual design. Interfaces people understand the first time they see them.'
          icon={
            <svg className='w-6 h-6' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
              <circle cx='12' cy='12' r='10' />
              <path d='M8 14s1.5 2 4 2 4-2 4-2' />
              <line x1='9' y1='9' x2='9.01' y2='9' />
              <line x1='15' y1='9' x2='15.01' y2='9' />
            </svg>
          }
        />
        <ServiceCard
          title='Technical leadership'
          description='Fractional CTO, architecture reviews, team scaling, and vendor selection. Senior guidance without the full-time overhead.'
          icon={
            <svg className='w-6 h-6' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
              <circle cx='12' cy='12' r='3' />
              <path d='M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z' />
            </svg>
          }
        />
      </div>
    </section>
  )
}

export default Services
